import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.3 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } },
};

const TourVideo = () => {
  const { scrollY } = useScroll();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Parallax for background circles
  const blueCircleY = useTransform(scrollY, [0, 500], [0, 50]);
  const pinkCircleY = useTransform(scrollY, [0, 500], [0, -50]);
  const blueCircleOpacity = useTransform(scrollY, [0, 300], [0.2, 0.3]);
  const pinkCircleOpacity = useTransform(scrollY, [0, 300], [0.2, 0.3]);

  // Scroll-triggered gradient shift
  const gradientShift = useTransform(
    scrollY,
    [0, 800],
    [
      "linear-gradient(to br, #bfdbfe, #eff6ff)",
      "linear-gradient(to br, #93c5fd, #dbeafe)",
    ]
  );

  return (
    <motion.section
      className="relative w-full py-16 sm:py-20 md:py-24 overflow-hidden"
      style={{ background: gradientShift }}
    >
      {/* Animated glowing background circles - properly contained */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className={`absolute top-0 left-0 w-60 h-60 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-full ${
            isMobile ? "bg-blue-200 blur-2xl" : "bg-blue-300 blur-3xl"
          }`}
          style={{ y: blueCircleY, opacity: blueCircleOpacity }}
          animate={
            isMobile
              ? { x: [0, 20, 0] }
              : {
                  x: [0, 40, 0],
                  boxShadow: [
                    "0 0 30px rgba(59, 130, 246, 0.3)",
                    "0 0 60px rgba(59, 130, 246, 0.5)",
                    "0 0 30px rgba(59, 130, 246, 0.3)",
                  ],
                }
          }
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute bottom-0 right-0 w-60 h-60 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-full ${
            isMobile ? "bg-pink-200 blur-2xl" : "bg-pink-300 blur-3xl"
          }`}
          style={{ y: pinkCircleY, opacity: pinkCircleOpacity }}
          animate={
            isMobile
              ? { x: [0, -20, 0] }
              : {
                  x: [0, -40, 0],
                  boxShadow: [
                    "0 0 30px rgba(236, 72, 153, 0.3)",
                    "0 0 60px rgba(236, 72, 153, 0.5)",
                    "0 0 30px rgba(236, 72, 153, 0.3)",
                  ],
                }
          }
          transition={{ repeat: Infinity, duration: 14, ease: "easeInOut" }}
        />
      </div>

      {/* Content container */}
      <motion.div
        className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 text-center flex flex-col items-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {/* Heading */}
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-8 sm:mb-10 md:mb-12 leading-tight px-4"
          variants={itemVariants}
        >
          Explore Our Campus
        </motion.h2>

        {/* Video container */}
        <motion.div 
          className="flex justify-center w-full" 
          variants={itemVariants}
        >
          <div className="relative group w-full max-w-4xl aspect-video rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden bg-white/20 backdrop-blur-lg border border-white/30 transition-transform duration-500 hover:scale-105">
            <iframe
              className="absolute inset-0 w-full h-full rounded-2xl sm:rounded-3xl"
              src="https://www.youtube.com/embed/HQdFQ86BBC8?autoplay=1&mute=1&controls=0&loop=1&playlist=HQdFQ86BBC8&modestbranding=1&rel=0"
              title="Campus Tour"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500 pointer-events-none" />
            
            {/* Play button overlay */}
            <a
              href="https://www.youtube.com/watch?v=HQdFQ86BBC8"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            >
              <motion.div
                className="bg-white/90 text-blue-600 px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base md:text-lg font-semibold shadow-lg hover:bg-white transition"
                initial={{ scale: 1, y: 0 }}
                animate={{
                  scale: [1, 1.05, 1],
                  y: isMobile ? [0, -4, 0] : [0, -8, 0],
                }}
                transition={{
                  repeat: Infinity,
                  repeatDelay: 2,
                  duration: 1.5,
                  ease: "easeInOut",
                }}
                whileHover={{ scale: 1.1, y: isMobile ? -3 : -5 }}
              >
                ▶ Play Full Video
              </motion.div>
            </a>
          </div>
        </motion.div>

        {/* CTA button */}
        <motion.button
          className="mt-10 sm:mt-12 px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-full bg-blue-600 text-white text-sm sm:text-base md:text-lg font-semibold shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all duration-300"
          variants={itemVariants}
          whileHover={{ scale: 1.08, y: -3 }}
          whileTap={{ scale: 0.95 }}
        >
          Join Our Community
        </motion.button>
      </motion.div>
    </motion.section>
  );
};

export default TourVideo;